import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Calendar, User, Mail, Phone, MapPin, FileText, Upload, Check } from 'lucide-react';

const CheckInForm = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    check_in_date: '',
    check_out_date: '',
    passport_number: '',
    accommodation_type: '',
    country: '',
    whatsapp_number: '',
    email: '',
    passport_picture: null,
    establishment_id: '',
    establishment_name: ''
  });

  const [accommodationTypes, setAccommodationTypes] = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [countries, setCountries] = useState([]);
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [upcomingSuggestions, setUpcomingSuggestions] = useState([]);

  useEffect(() => {
    loadAccommodationTypes();
    loadCountries();
    loadEstablishments();
    checkForToken();
  }, []);

  const loadEstablishments = async () => {
    try {
      const res = await axios.get(`${API}/establishments`);
      setEstablishments(res.data);
    } catch (error) {
      console.error('Error loading establishments:', error);
    }
  };

  const loadAccommodationTypes = async () => {
    try {
      const res = await axios.get(`${API}/accommodation-types`);
      setAccommodationTypes(res.data);
    } catch (error) {
      console.error('Error loading accommodation types:', error);
    }
  };

  const loadCountries = async () => {
    try {
      const res = await axios.get(`${API}/countries`);
      setCountries(res.data);
    } catch (error) {
      console.error('Error loading countries:', error);
    }
  };

  const checkForToken = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    
    if (token) {
      try {
        const res = await axios.get(`${API}/check-in/prefill/${token}`);
        setFormData(prev => ({
          ...prev,
          full_name: res.data.guest_name,
          check_in_date: res.data.check_in_date
        }));
        toast.success('Form pre-filled!');
      } catch (error) {
        console.error('Error loading prefill data:', error);
      }
    }
  };

  const searchUpcoming = async (name) => {
    if (name.length < 2) {
      setUpcomingSuggestions([]);
      return;
    }

    try {
      const res = await axios.get(`${API}/check-in/upcoming?name=${name}`);
      setUpcomingSuggestions(res.data);
    } catch (error) {
      console.error('Error searching upcoming:', error);
    }
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, full_name: value });
    searchUpcoming(value);
  };

  const selectSuggestion = (suggestion) => {
    setFormData(prev => ({
      ...prev,
      full_name: suggestion.guest_name,
      check_in_date: suggestion.check_in_date
    }));
    setUpcomingSuggestions([]);
    toast.success('Information auto-filled!');
  };

  const handleCountrySearch = (value) => {
    setCountrySearch(value);
    setFormData({ ...formData, country: value });
    setShowCountryDropdown(true);
  };

  const selectCountry = (country) => {
    setFormData({ ...formData, country });
    setCountrySearch(country);
    setShowCountryDropdown(false);
  };

  const filteredCountries = countries.filter(country =>
    country.toLowerCase().includes(countrySearch.toLowerCase())
  );

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, passport_picture: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/check-in`, formData);
      setSubmitted(true);
      toast.success('Check-in completed successfully!');
    } catch (error) {
      toast.error('Failed to complete check-in');
      console.error('Check-in error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Check-in Complete!</h2>
          <p className="text-gray-600 mb-4">Welcome to Wostel Djerba! 🏝️</p>
          <p className="text-sm text-gray-500 mb-6">A confirmation email has been sent to {formData.email}</p>
          <button
            onClick={() => {
              setSubmitted(false);
              setFormData({
                full_name: '',
                check_in_date: '',
                check_out_date: '',
                passport_number: '',
                accommodation_type: '',
                country: '',
                whatsapp_number: '',
                email: '',
                passport_picture: null,
                establishment_id: '',
                establishment_name: ''
              });
            }}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            New Check-in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Wostel Djerba</h1>
          <p className="text-xl text-gray-600">Guest Check-in Form</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Establishment *
              </label>
              <select
                required
                value={formData.establishment_id}
                onChange={(e) => {
                  const est = establishments.find(x => x.id === e.target.value);
                  setFormData({ ...formData, establishment_id: e.target.value, establishment_name: est?.name || '' });
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                data-testid="checkin-establishment-select"
              >
                <option value="">Select establishment</option>
                {establishments.map((est) => (
                  <option key={est.id} value={est.id}>{est.name}</option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2 relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User className="inline w-4 h-4 mr-1" /> Full Name *
              </label>
              <input
                type="text"
                required
                value={formData.full_name}
                onChange={handleNameChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="John Doe"
              />
              {upcomingSuggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {upcomingSuggestions.map((suggestion) => (
                    <div
                      key={suggestion.id}
                      onClick={() => selectSuggestion(suggestion)}
                      className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                    >
                      <div className="font-medium">{suggestion.guest_name}</div>
                      <div className="text-sm text-gray-500">
                        {suggestion.booking_platform} - {suggestion.check_in_date}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="inline w-4 h-4 mr-1" /> Check-in Date *
              </label>
              <input
                type="date"
                required
                value={formData.check_in_date}
                onChange={(e) => setFormData({ ...formData, check_in_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="inline w-4 h-4 mr-1" /> Check-out Date *
              </label>
              <input
                type="date"
                required
                value={formData.check_out_date}
                onChange={(e) => setFormData({ ...formData, check_out_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FileText className="inline w-4 h-4 mr-1" /> Passport Number *
              </label>
              <input
                type="text"
                required
                value={formData.passport_number}
                onChange={(e) => setFormData({ ...formData, passport_number: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="AB123456"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Accommodation Type *
              </label>
              <select
                required
                value={formData.accommodation_type}
                onChange={(e) => setFormData({ ...formData, accommodation_type: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select type</option>
                {accommodationTypes.map((type) => (
                  <option key={type.id} value={type.name}>{type.name}</option>
                ))}
              </select>
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="inline w-4 h-4 mr-1" /> Country *
              </label>
              <input
                type="text"
                required
                value={countrySearch || formData.country}
                onChange={(e) => handleCountrySearch(e.target.value)}
                onFocus={() => setShowCountryDropdown(true)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Search country..."
                autoComplete="off"
              />
              {showCountryDropdown && filteredCountries.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredCountries.slice(0, 50).map((country, index) => (
                    <div
                      key={index}
                      onClick={() => selectCountry(country)}
                      className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                    >
                      {country}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Phone className="inline w-4 h-4 mr-1" /> WhatsApp Number *
              </label>
              <input
                type="tel"
                required
                value={formData.whatsapp_number}
                onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="+33612345678"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="inline w-4 h-4 mr-1" /> Email *
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="john@example.com"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Upload className="inline w-4 h-4 mr-1" /> Passport Photo (Optional)
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              {formData.passport_picture && (
                <p className="text-sm text-green-600 mt-2">✓ Photo uploaded</p>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full mt-8 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Submitting...' : 'Complete Check-in'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CheckInForm;
